import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Mail,
  Share2,
  Facebook,
  Instagram,
  Youtube,
  Loader2,
  CheckCircle2,
  ArrowRight,
  Zap,
  Newspaper,
  Scale,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ShareDialog } from '@/components/share/ShareDialog';
import { buildSocialUrl } from '@/lib/social-links';
import { trackFollowClick } from '@/lib/follow-prompt';
import { HOME_CAMPAIGN, markHomeCampaignSeen } from '@/config/homeCampaign';
import { trackEvent } from '@/lib/analytics/ga';

interface CampaignTakeoverProps {
  /** Kaldes når laget lukkes, så forsiden kan vises igen. */
  onClose: () => void;
}

const SOCIALS = [
  { channel: 'facebook' as const, Icon: Facebook, label: 'Facebook' },
  { channel: 'instagram' as const, Icon: Instagram, label: 'Instagram' },
  { channel: 'youtube' as const, Icon: Youtube, label: 'YouTube' },
];

/**
 * Fuldskærms-kampagne på forsiden i kampagneperioden.
 * Vises én gang pr. browser og har altid en synlig vej videre til forsiden.
 */
export function CampaignTakeover({ onClose }: CampaignTakeoverProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Lås sidens scroll mens laget er åbent.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    trackEvent('home_campaign_view', { placement: HOME_CAMPAIGN.placement });
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const close = () => {
    markHomeCampaignSeen();
    onClose();
  };

  const submitNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Ugyldig email');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('newsletter-subscribe', {
        body: { email: trimmed },
      });
      if (error) throw error;
      setDone(true);
      markHomeCampaignSeen();
      trackEvent('home_campaign_signup', { placement: HOME_CAMPAIGN.placement });
      toast.success('Tjek din indbakke for at bekræfte tilmeldingen');
    } catch (err: any) {
      toast.error(err?.message || 'Kunne ikke tilmelde – prøv igen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Velkommen til Min Nye Elbil"
        className="fixed inset-0 z-50 overflow-y-auto bg-background"
      >
        {/* Blød accent-baggrund som resten af sitets identitet */}
        <div className="absolute inset-0 bg-gradient-to-b from-accent/15 via-background to-background" aria-hidden="true" />

        <div
          className="relative min-h-[100dvh] flex flex-col"
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Top: logo som vej videre */}
          <div className="container mx-auto px-4 pt-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={close}
              className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-accent transition-colors min-h-11"
            >
              <img src="/logo.svg" alt="Min Nye Elbil" className="h-7 w-auto" />
            </button>
            <Button variant="ghost" onClick={close} className="min-h-11 gap-1.5 text-muted-foreground">
              Videre til elbilerne
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="container mx-auto px-4 flex-1 flex items-center py-8 sm:py-12">
            <div className="mx-auto w-full max-w-xl text-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent">
                <Sparkles className="w-3.5 h-3.5" />
                Helt ny side
              </span>

              {done ? (
                <>
                  <h1 className="font-display text-3xl sm:text-4xl font-bold mt-5 leading-tight">
                    Tak – du er næsten med!
                  </h1>
                  <p className="mt-3 text-base text-muted-foreground">
                    Tjek din indbakke og bekræft tilmeldingen. Følg os også, så du ikke går glip af
                    nye elbiler, priser og tests.
                  </p>
                </>
              ) : (
                <>
                  <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mt-5 leading-tight">
                    Min Nye Elbil er helt ny
                  </h1>
                  <p className="mt-3 text-base sm:text-lg text-muted-foreground">
                    Danmarks uafhængige elbil-overblik – priser, rækkevidde og tests samlet ét sted.
                    Vær med fra starten: få nyhedsbrevet og følg os.
                  </p>
                </>
              )}

              {/* Nyhedsbrev */}
              {done ? (
                <div className="mt-6 flex items-center justify-center gap-2 rounded-xl border bg-card px-4 py-3 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                  <span>Tilmeldingen er sendt til {email}</span>
                </div>
              ) : (
                <form onSubmit={submitNewsletter} className="mt-6 flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Mail className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <Input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="din@email.dk"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      aria-label="Email-adresse til nyhedsbrev"
                      className="h-12 pl-9 bg-background text-base"
                    />
                  </div>
                  <Button type="submit" variant="accent" disabled={loading} className="h-12 px-6 text-base">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tilmeld'}
                  </Button>
                </form>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Ét nyhedsbrev om måneden. Ingen spam – afmeld når du vil.
              </p>

              {/* Følg os */}
              <div className="mt-8">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                  Følg os
                </p>
                <div className="flex items-center justify-center gap-3">
                  {SOCIALS.map(({ channel, Icon, label }) => (
                    <a
                      key={channel}
                      href={buildSocialUrl(channel, HOME_CAMPAIGN.placement)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Følg Min Nye Elbil på ${label}`}
                      onClick={() => {
                        trackFollowClick(channel, HOME_CAMPAIGN.placement);
                        trackEvent('home_campaign_follow', { channel });
                        markHomeCampaignSeen();
                      }}
                      className="inline-flex items-center justify-center min-w-11 min-h-11 rounded-xl border bg-card text-foreground hover:border-accent hover:text-accent transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  ))}
                  <span aria-hidden="true" className="mx-1 h-8 w-px bg-border" />
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShareOpen(true);
                      markHomeCampaignSeen();
                    }}
                    className="min-h-11 gap-2"
                  >
                    <Share2 className="w-4 h-4" />
                    Del siden
                  </Button>
                </div>
              </div>

              {/* Vej videre */}
              <div className="mt-10">
                <Button variant="accent" size="lg" onClick={close} className="min-h-12 gap-2 w-full sm:w-auto px-8">
                  Videre til elbilerne
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
                  <Link to="/biler" onClick={close} className="inline-flex items-center gap-1.5 hover:text-accent">
                    <Zap className="w-3.5 h-3.5" /> Alle elbiler
                  </Link>
                  <Link to="/sammenlign" onClick={close} className="inline-flex items-center gap-1.5 hover:text-accent">
                    <Scale className="w-3.5 h-3.5" /> Sammenlign
                  </Link>
                  <Link to="/nyheder" onClick={close} className="inline-flex items-center gap-1.5 hover:text-accent">
                    <Newspaper className="w-3.5 h-3.5" /> Nyheder
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        title="Min Nye Elbil – find og sammenlign elbiler"
        url={typeof window !== 'undefined' ? window.location.origin + '/' : 'https://minnyeelbil.dk/'}
        context="generic"
      />
    </>
  );
}
